/**
 * Task-Specific Actions
 * Actions defined specifically for the tasks entity
 * Strongly typed to Contact_Tasks row type
 * Uses shared builders to eliminate duplication
 */

import type { Contact_Tasks } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import { ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  getTaskDirectContactContextId,
  isTaskLinkedToCommunications,
} from "@/lib/utils/crm/task-linked-entity";
import {
  universalDeleteAction,
  universalEditAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalMakeTicketAction,
  universalLogTimeEntryAction,
  universalViewCompanyAction,
  universalViewDetailsAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createDuplicateTaskAction,
  createQuickCompleteAction,
  createCompleteTaskAction,
  createAddTaskNoteAction,
  createSetDueDateAction,
  createAssignTaskToMeAction,
} from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";

type TaskLogActivityContext = Contact_Tasks & {
  effectiveLogActivityContactId?: string;
  communicationContactLoading?: boolean;
};

function createTaskLogActivityAction(): DetailActionStrict<Contact_Tasks> {
  const base = universalLogActivityAction<Contact_Tasks>();
  return {
    ...base,
    visible: (task) => {
      const t = task as TaskLogActivityContext;
      if (t.effectiveLogActivityContactId) return true;
      if (getTaskDirectContactContextId(t)) return true;
      if (isTaskLinkedToCommunications(t.entity_type) && t.entity_id) return true;
      return false;
    },
    disabled: (task) => {
      const t = task as TaskLogActivityContext;
      if (t.communicationContactLoading) return true;
      if (isTaskLinkedToCommunications(t.entity_type) && !t.effectiveLogActivityContactId) {
        return true;
      }
      return false;
    },
  };
}

/**
 * Complete task actions array
 * Combines universal and task-specific actions in the correct order
 * Order matches the original EnhancedTaskDetailConfig.ts for behavior preservation
 */
export const taskDetailActions: DetailActionStrict<Contact_Tasks>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Contact_Tasks>(),

  // PRIMARY ACTIONS (Header)
  createStatusAction<Contact_Tasks>(),
  universalEditAction<Contact_Tasks>(),
  createDuplicateTaskAction<Contact_Tasks>(),
  createAssignTaskToMeAction<Contact_Tasks>(),
  universalSendEmailAction<Contact_Tasks>(),
  // Template-prefilled text — opens the send-text modal with a task template,
  // pre-seeding the task's linked contact when one exists.
  {
    id: "send-text-template",
    label: "Text from template",
    icon: "message-square",
    variant: "outline",
    action: "sendTextTemplate",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: false,
    tooltip: "Send a templated text message — pick the contact and a ready-made message.",
  },
  createTaskLogActivityAction(),
  createAddTaskNoteAction<Contact_Tasks>(),
  createSetDueDateAction<Contact_Tasks>(),

  // TABLE ONLY ACTIONS
  universalViewDetailsAction<Contact_Tasks>("viewTask"),
  createQuickCompleteAction<Contact_Tasks>(),

  // UNIVERSAL ACTIONS (Both Contexts)
  universalMakeTicketAction<Contact_Tasks>(),
  universalLogTimeEntryAction<Contact_Tasks>(),
  universalViewCompanyAction<Contact_Tasks>(),
  universalConfigureAutomatedTasksAction<Contact_Tasks>(),
  createSendToSlackAction<Contact_Tasks>(),
  createSendToSalesChannelAction<Contact_Tasks>(),
  createOpenSlackChannelAction<Contact_Tasks>(),
  createCompleteTaskAction<Contact_Tasks>(),
  universalDeleteAction<Contact_Tasks>(),
];
